// Developer: Ankit Gupta
/**
 * Global Background Jobs & Active Tasks Hook.
 *
 * Tracks Celery workers, pipeline migration runs, and background tasks
 * across DataPilot in real-time. Automatically adapts polling frequency:
 * high-frequency (4s) when active jobs are in flight, low-frequency (30s)
 * when idle.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { message } from 'antd';
import i18n from '@/i18n';

export interface ActiveJobRun {
  id: number;
  project_id: number;
  source_environment_id: number | null;
  target_environment_id: number | null;
  entity_group_id: number | null;
  run_status: string;
  run_mode: string | null;
  remarks: string | null;
  extract_flag: string | null;
  transform_flag: string | null;
  validate_flag: string | null;
  import_flag: string | null;
  recon_flag: string | null;
  fus_extract_flag: string | null;
  email_notify_flag: string | null;
  last_run_duration: number | null;
  duration_min: number | null;
  error_detail: string | null;
  data_volume: string | null;
  created_at?: string;
  updated_at?: string;
}

interface RunListResponse {
  items: ActiveJobRun[];
  total: number;
}

export const ACTIVE_RUN_STATUSES = new Set([
  'REQUESTED',
  'IN_PROGRESS',
  'EXTRACTING',
  'TRANSFORMING',
  'VALIDATING',
  'IMPORTING',
  'RECONCILING',
]);

export function useActiveJobs() {
  const qc = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['global-active-jobs'],
    queryFn: () =>
      api.get<RunListResponse>('/runs', {
        page: 1,
        page_size: 15,
      }),
    refetchInterval: (query) => {
      const raw = query.state.data as RunListResponse | undefined;
      const list = raw?.items ?? [];
      const hasActive = list.some((r) =>
        ACTIVE_RUN_STATUSES.has((r.run_status || '').toUpperCase())
      );
      // Fast polling (4s) when active pipelines are processing, relaxed (30s) when idle
      return hasActive ? 4000 : 30000;
    },
    refetchOnWindowFocus: true,
  });

  const allRuns = data?.items ?? [];

  const activeRuns = allRuns.filter((r) =>
    ACTIVE_RUN_STATUSES.has((r.run_status || '').toUpperCase())
  );

  const recentRuns = allRuns
    .filter((r) => !ACTIVE_RUN_STATUSES.has((r.run_status || '').toUpperCase()))
    .slice(0, 6);

  const cancelMutation = useMutation({
    mutationFn: ({ projectId, runId }: { projectId: number; runId: number }) =>
      api.post(`/projects/${projectId}/runs/${runId}/cancel`),
    onSuccess: (_, vars) => {
      message.success(i18n.t('shell:tasks.cancelRequested', { id: vars.runId }));
      qc.invalidateQueries({ queryKey: ['global-active-jobs'] });
      qc.invalidateQueries({ queryKey: ['runs'] });
      qc.invalidateQueries({ queryKey: ['run-detail', String(vars.projectId), String(vars.runId)] });
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.detail ?? i18n.t('shell:tasks.cancelFailed'));
    },
  });

  return {
    allRuns,
    activeRuns,
    recentRuns,
    activeCount: activeRuns.length,
    hasActive: activeRuns.length > 0,
    isLoading,
    isFetching,
    refetch,
    cancelRun: (projectId: number, runId: number) =>
      cancelMutation.mutate({ projectId, runId }),
    isCancelling: cancelMutation.isPending,
  };
}
